import type { AssignmentItem, TransportRequest } from './Types'

export function getTransportRequest(a: AssignmentItem): TransportRequest | null | undefined {
    return a.executor?.vehicleNeed?.transportRequest
}

// Вантаж: пріоритет — власне поле завдання (standalone-задачі несуть його самі,
// без заявки), далі потреба, далі заявка. cargoTitle — плоске поле з бекенду на
// випадок, коли вкладений cargo згорнувся до @id через дедуплікацію в графі.
export function getCargo(a: AssignmentItem): string | null {
    return a.cargo?.title
        || a.cargoTitle
        || a.executor?.vehicleNeed?.cargo?.title
        || getTransportRequest(a)?.cargo?.title
        || null
}

// Пункт навантаження: пріоритет — поле самого завдання, далі заявка, далі
// vehicleNeed (standalone-завдання мають свій loadingPoint без заявки).
export function getLoadingPoint(a: AssignmentItem): string | null {
    return a.loadingPoint?.title
        || getTransportRequest(a)?.loadingPoint?.title
        || a.executor?.vehicleNeed?.loadingPoint?.title
        || null
}

export function getUnloadingPoint(a: AssignmentItem): string | null {
    return a.unloadingPoint?.title
        || getTransportRequest(a)?.unloadingPoint?.title
        || a.executor?.vehicleNeed?.unloadingPoint?.title
        || null
}

// Дата та час створення (ISO-8601). У деталі — createdAt, у списку — createdDate.
export function getCreatedAt(a: AssignmentItem): string | null {
    return a.createdAt || a.createdDate || null
}

/** Форматує ISO-8601 у локаль uk-UA «16.06.2026, 14:30». null → null. */
export function formatDateTime(iso?: string | null): string | null {
    if (!iso) return null
    const d = new Date(iso)
    if (isNaN(d.getTime())) return null
    return d.toLocaleString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export function getDispatcherPhone(a: AssignmentItem): string | null {
    return a.executor?.dispatcher?.phone
        || a.responsibleDispatcherPhone
        || getTransportRequest(a)?.responsibleDispatcher?.phone
        || null
}

/** Saved map link for the assignment, opened by «Прокласти маршрут». */
export function getRouteUrl(a: AssignmentItem): string | null {
    // Спершу денормалізована копія на завданні; якщо порожня (напр. маршрут
    // обрано вручну і бекенд не скопіював посилання) — фолбек на посилання
    // самого привʼязаного типового маршруту, який бекенд віддає вкладено.
    return a.routeUrl || a.typicalRoute?.routeUrl || null
}

export function getDispatcherName(a: AssignmentItem): string | null {
    return a.executor?.dispatcher?.fullName
        || a.responsibleDispatcherName
        || getTransportRequest(a)?.responsibleDispatcher?.fullName
        || null
}

// Відповідальний замовник: пріоритет — плоскі поля з бекенду, бо вкладений
// responsibleCustomer часто згортається до @id через дедуплікацію однакових
// User у графі (див. ExecutorAssignmentEntity::getResponsibleCustomerName).
export function getCustomerPhone(a: AssignmentItem): string | null {
    return a.responsibleCustomerPhone
        || a.responsibleCustomer?.phone
        || getTransportRequest(a)?.responsibleCustomer?.phone
        || null
}

export function getCustomerName(a: AssignmentItem): string | null {
    return a.responsibleCustomerName
        || a.responsibleCustomer?.fullName
        || getTransportRequest(a)?.responsibleCustomer?.fullName
        || null
}

/** Parent transport-request status, e.g. 'cancelled' / 'completed'. */
export function getRequestStatus(a: AssignmentItem): string | null {
    return getTransportRequest(a)?.status ?? null
}

/**
 * A cancelled/completed parent request freezes the task: the driver must not be
 * able to start or advance execution (BUG-2). Mirrors the server-side guard so
 * the action button is hidden before the driver even tries.
 */
export function isRequestFrozen(a: AssignmentItem): boolean {
    const status = getRequestStatus(a)
    return status === 'cancelled' || status === 'completed'
}
